import { KeyStatusCounts } from "../../store/types";

/** A key needs at least this many active rows before it gets a band. */
export const MinActiveRowsForRag = 5;
/** After the first completion, show "resolving" for this long before a colour. */
export const RagGraceMinutes = 10;
/** Red when at least one has completed and the completed share is at most this. */
export const RagRedMaxPercent = 20;
/** Green when the completed share is at least this. */
export const RagGreenMinPercent = 80;

export type RagBand = "none" | "resolving" | "red" | "amber" | "green";

export type NoIconReason = "nothingCompleted" | "notEnoughRows" | "firstCompletionUnknown";

const MinuteMs = 60_000;

/**
 * Age of the first completion, from server times only (asOf and firstCompletedAt).
 * Null when firstCompletedAt is missing or either time is unparseable.
 */
const firstCompletionAgeMs = (entry: KeyStatusCounts, asOf: string): number | null => {
  const age = entry.firstCompletedAt ? Date.parse(asOf) - Date.parse(entry.firstCompletedAt) : NaN;
  return Number.isNaN(age) ? null : age;
};

/** Why a loaded key has no icon, or null when it has one. */
export function noIconReason(entry: KeyStatusCounts, asOf: string): NoIconReason | null {
  if (entry.counts.Completed === 0) {
    return "nothingCompleted";
  }

  if (entry.active < MinActiveRowsForRag) {
    return "notEnoughRows";
  }

  // Without a first-completion time the grace window can't be judged, so no colour.
  if (firstCompletionAgeMs(entry, asOf) === null) {
    return "firstCompletionUnknown";
  }

  return null;
}

/**
 * The marker band for a key. Check order: nothing completed, then too few active rows
 * (this takes precedence over resolving), then an unknown first-completion time, then the
 * grace window, then the colour. Integer comparisons only, so exactly 20% and 80% are not
 * subject to rounding.
 */
export function ragBand(entry: KeyStatusCounts, asOf: string): RagBand {
  if (noIconReason(entry, asOf) !== null) {
    return "none";
  }

  const completed = entry.counts.Completed;
  const active = entry.active;
  const age = firstCompletionAgeMs(entry, asOf);

  if (age !== null && age < RagGraceMinutes * MinuteMs) {
    return "resolving";
  }

  if (completed * 100 <= active * RagRedMaxPercent) {
    return "red";
  }

  if (completed * 100 >= active * RagGreenMinPercent) {
    return "green";
  }

  return "amber";
}

/** Whole minutes (rounded up) until a resolving key gets its colour, measured against asOf. */
export function minutesUntilColour(entry: KeyStatusCounts, asOf: string): number {
  const age = firstCompletionAgeMs(entry, asOf) ?? 0;
  return Math.max(1, Math.ceil((RagGraceMinutes * MinuteMs - age) / MinuteMs));
}

/** Completed share as a whole percentage, for display only (never used for the band). */
export function completedPercent(entry: KeyStatusCounts): number {
  return entry.active > 0 ? Math.round((100 * entry.counts.Completed) / entry.active) : 0;
}

/** "4m", "52m", "1h 05m", "2d 3h"; "<1m" under a minute. */
export function formatAgo(ms: number): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / MinuteMs);

  if (totalMinutes < 1) {
    return "<1m";
  }

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  return `${minutes}m`;
}

/** Local HH:mm of an ISO time. */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
